// Web display ads (Mediavine). The wrapper script is STATIC in index.html's
// head and in the blog/guide shell, so nothing here loads it. What lives here
// is everything the app has to tell the wrapper: the settings div's policy
// (APP_ROUTE_BLOCKLIST), the Pro suppression flag that puts
// data-blocklist-all on that div at first paint, and the CMP reopen for the
// footer's "Privacy choices" button. Ads serve nothing until the site is
// launched in the Mediavine dashboard, so all of this ships dark.
//
// Placement policy (Keith, 2026-09-12): adhesion, sticky sidebar on desktop,
// one placed in-content unit per result. Interstitials, page skins and recipe
// widgets off. The corner video is off on app routes on PHONES only. On
// desktop it runs (bottom-right); the Deck Chat dock stacks above it
// (--ad-corner-clear), toasts and the decks saving indicator anchor
// bottom-LEFT, and
// fixed menus avoid it via `adObstacleRects` (src/lib/adInset.ts).
// Adhesion runs on every device and every app route (Keith, 2026-10-08,
// after a one-week /bracket-only phone trial). Mediavine's policy is that the
// site never moves or covers the unit, so OUR fixed bottom UI (tab bar, Deck
// Chat pill and composer, toasts, overlays, menus) rides above it on
// --ad-inset-bottom (src/lib/adInset.ts); src/__tests__/overlaysAboveAd.test.ts
// fails any new bottom-pinned element that does not. Blog and guide pages
// carry no settings div and keep every unit.
//
// COUPLING: Pro suppression rides on useProStatus, which only reads
// /pro/status for a signed-in user with a resolved session. Signed out,
// nobody is Pro, so nobody is exempt from ads.

export type AdPlacement = 'bracket_lead' | 'bracket_mid' | 'build_result'

/**
 * The in-content switch, separate from the Pro/entitlement gate. False keeps
 * every AdHint mount dark regardless of `adsEnabled`, matching index.html's
 * settings div, which still carries `data-blocklist-content-desktop` and
 * `data-blocklist-content-mobile` in APP_ROUTE_BLOCKLIST above. The two flip
 * together: flipping this to true without also dropping those two attributes
 * off the blocklist re-creates the auto-inserted units on hint-less routes
 * that APP_ROUTE_BLOCKLIST's comment warns about.
 */
export const IN_CONTENT_ADS_ON = false

/** The settings div's id, static in index.html before #root. */
export const MEDIAVINE_SETTINGS_ID = 'mediavine-settings'

/**
 * Build-time emergency kill switch (VITE_ADS_KILL=1). The inline script in
 * index.html sets data-blocklist-all from the same var, and this define is
 * what stops the Pro sync from taking it back off for a signed-out or free
 * visitor. Cheaper than waiting on Mediavine to un-launch.
 */
export const ADS_KILL: boolean = __ADS_KILL__

/**
 * The closed list of blocklist attributes the settings div carries on app
 * routes, in index.html order. Exported so src/__tests__/indexHtml.test.ts
 * can pin the served HTML to it - the div is outside React, so nothing else
 * would notice a hand edit drifting from the policy.
 *
 * content-* IS blocked for launch. The wrapper auto-inserts in-content after
 * arbitrary blocks and Mediavine says it "should run after your page content
 * has finished rendering", which a React site cannot promise; routes with no
 * placed hint (Home, the /bracket form, /decks, /rules, chat) would get units
 * in the tool UI. The AdHint mounts stay wired but DORMANT until support
 * confirms the SPA rescan lifecycle, at which point the two content entries
 * come off this list and the hints take over.
 *
 * Known gap, accepted 2026-10-08 (matches desktop): a Pro user with no cached
 * cb.adsOff (new device, cleared storage) can see phone adhesion for that
 * first session, until MediavineProSync's guarded reload or the next full
 * load. A real fix needs a server-known Pro signal before the wrapper runs.
 */
export const APP_ROUTE_BLOCKLIST = [
  'data-blocklist-interstitial-mobile',
  'data-blocklist-interstitial-desktop',
  'data-blocklist-universal-player-mobile',
  'data-blocklist-gumgum-skins',
  'data-blocklist-recipe',
  'data-blocklist-chicory',
  'data-blocklist-zergnet',
  'data-blocklist-content-desktop',
  'data-blocklist-content-mobile',
] as const

/** localStorage key the inline script in index.html reads at first paint. */
export const ADS_OFF_KEY = 'cb.adsOff'

const BLOCK_ALL_ATTR = 'data-blocklist-all'

/**
 * Whether the inline script in index.html put data-blocklist-all on the
 * settings div at FIRST PAINT (cached Pro flag, or the kill switch). Read
 * once, when this module is evaluated: that is after the inline script and
 * before React mounts MediavineProSync, the only thing that ever changes the
 * attribute later. False when there is no div (tests, a shell with no ads).
 */
const BLOCKED_AT_BOOT: boolean = (() => {
  try {
    return typeof document !== 'undefined' &&
      document.getElementById(MEDIAVINE_SETTINGS_ID)?.hasAttribute(BLOCK_ALL_ATTR) === true
  } catch {
    return false
  }
})()

export function adsBlockedAtBoot(): boolean {
  return BLOCKED_AT_BOOT
}

/**
 * THE predicate. Tri-state on purpose and in the OPPOSITE direction from
 * `offersPurchase`: only an explicit Pro entitlement hides ads. Unknown,
 * loading, failed and logged-out all show them. No surface may reimplement
 * this inline - AdHint.test.tsx stubs it to disagree with the raw field.
 */
export function adsEnabled(status: { isPro?: boolean } | null | undefined): boolean {
  return status?.isPro !== true
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function defaultStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    // Safari with storage disabled throws on the mere property read.
    return null
  }
}

/** True when the cached flag says the last resolved entitlement was Pro. */
export function readAdsOffFlag(storage: StorageLike | null = defaultStorage()): boolean {
  try {
    return storage?.getItem(ADS_OFF_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Cache the resolved entitlement for the NEXT page load's inline script.
 * Off means the key is removed, not set to "0": the inline script only
 * tests for "1", and a stale key is exactly the ex-Pro bug this exists to
 * close. Every storage access is guarded because private mode and quota
 * both throw, and an ad flag is never worth an error.
 */
export function writeAdsOffFlag(off: boolean, storage: StorageLike | null = defaultStorage()): void {
  try {
    if (off) storage?.setItem(ADS_OFF_KEY, '1')
    else storage?.removeItem(ADS_OFF_KEY)
  } catch {
    // Nothing to do: the next visit falls back to the network read.
  }
}

/**
 * Set or clear data-blocklist-all on the settings div for THIS page load.
 * No-op when the div is absent (tests, or a shell that has no ads), so a
 * caller never has to know which page it is on. Under the kill switch the
 * attribute is never REMOVED: the Pro sync clears it for signed-out and
 * free visitors, and that clear used to strip the block the inline script
 * had just set, so the switch only held until React booted.
 */
export function applyAdsBlocklist(off: boolean, doc: Document = document, kill: boolean = ADS_KILL): void {
  const el = doc.getElementById(MEDIAVINE_SETTINGS_ID)
  if (!el) return
  if (off || kill) el.setAttribute(BLOCK_ALL_ATTR, '1')
  else el.removeAttribute(BLOCK_ALL_ATTR)
}

export interface AdsOffInputs {
  /** useSupabaseToken().loading: the initial session read is in flight. */
  loading: boolean
  userId: string | null
  /** useProStatus().status */
  status: 'pending' | 'error' | 'success'
  isPro?: boolean
}

/**
 * What MediavineProSync should do with the flag, as a pure decision so the
 * four auth/status states are testable without React:
 *  - null:  leave the cached flag alone (session still loading, or signed in
 *           with no resolved entitlement yet - pending or errored)
 *  - false: signed out, so clear it. Without this an ex-Pro who signs out
 *           keeps the flag forever, because useProStatus is disabled without
 *           a user and would never resolve a "not Pro".
 *  - true/false from the predicate once the entitlement resolved.
 */
export function adsOffDecision({ loading, userId, status, isPro }: AdsOffInputs): boolean | null {
  if (loading) return null
  if (!userId) return false
  if (status === 'success') return !adsEnabled({ isPro: isPro === true })
  return null
}

/**
 * sessionStorage marker: this tab already reloaded once to drop ads for THIS
 * user. It stores the user id, so only the same account is held back; a
 * signed-out or free answer clears it (clearProReloadMarker), so a
 * Pro -> signed out -> Pro cycle in one tab can reload again. Per tab
 * session, so a reload that somehow still serves ads can never loop.
 */
export const ADS_OFF_RELOADED_KEY = 'cb.adsOffReloaded'

/**
 * Hard deadline, measured from navigation start (performance.now()), for
 * the entitlement to arrive and still trigger the reload. Past it the page
 * has been usable long enough that a reload would cost the reader more than
 * the ad does; the flag is written either way, so the next load is clean.
 */
export const PRO_RELOAD_DEADLINE_MS = 8_000

// What Journey leaves on a page it filled (DOM vocabulary, not copy).
const MV_LOADED_CLASS = 'mv-loaded'
const MV_ADHESION_CONTAINER_ID = 'fixed_container_bottom'
const MV_PLAYER_ID = 'universalPlayer_wrapper'
const MV_ADUNIT_SELECTOR = '.adunit'

/**
 * True when the Mediavine wrapper has actually put ads on THIS page: body
 * class `mv-loaded`, the adhesion container, an ad unit, or the video
 * player. An ad blocker, the kill switch or a missing script leave none of
 * these, and then there is nothing a reload would remove.
 */
export function adsHaveLoaded(doc: Document = document): boolean {
  try {
    return doc.body.classList.contains(MV_LOADED_CLASS) ||
      !!doc.getElementById(MV_ADHESION_CONTAINER_ID) ||
      !!doc.getElementById(MV_PLAYER_ID) ||
      !!doc.querySelector(MV_ADUNIT_SELECTOR)
  } catch {
    return false
  }
}

// The interactions that permanently disarm the auto reload for this page
// load: anything that suggests the reader has started doing something a
// reload would throw away.
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'input', 'paste', 'submit', 'touchstart']

/**
 * The flag index.html's inline interaction sentinel sets on window. That
 * script runs before #root, so it sees typing and paste into the prerendered
 * fields that happen before this bundle has even been fetched.
 */
export const INTERACTION_FLAG = '__cbInteracted'

export interface InteractionWatch {
  interacted(): boolean
  dispose(): void
}

/**
 * Listen, in the capture phase, for the first user interaction and remember
 * it. Only TRUSTED events count by default (a script's synthetic click is
 * not the reader); `trustedOnly: false` exists for tests, since jsdom's
 * dispatched events are never trusted. Listeners come off at the first hit.
 */
export function createInteractionWatch(
  target: EventTarget,
  { trustedOnly = true }: { trustedOnly?: boolean } = {},
): InteractionWatch {
  let hit = false
  const opts: AddEventListenerOptions = { capture: true, passive: true }
  const dispose = () => {
    for (const type of INTERACTION_EVENTS) target.removeEventListener(type, onEvent, opts)
  }
  function onEvent(e: Event) {
    if (trustedOnly && !e.isTrusted) return
    hit = true
    dispose()
  }
  for (const type of INTERACTION_EVENTS) target.addEventListener(type, onEvent, opts)
  return { interacted: () => hit, dispose }
}

// Installed when this module is evaluated, which is at boot (MediavineProSync
// imports it from the root App), before the first render can take input.
const bootInteraction: InteractionWatch | null = (() => {
  try {
    return typeof window === 'undefined' ? null : createInteractionWatch(window)
  } catch {
    return null
  }
})()

/**
 * Whether the reader has interacted with this page load at all: the inline
 * sentinel's flag (covers the pre-boot window) OR this module's own watch
 * (covers the case where the sentinel is missing or threw).
 */
export function userHasInteracted(win: Window | undefined = typeof window === 'undefined' ? undefined : window): boolean {
  const flagged = !!win && (win as unknown as Record<string, unknown>)[INTERACTION_FLAG] === true
  return flagged || (bootInteraction?.interacted() ?? false)
}

/** Milliseconds since navigation start; Infinity where unknowable (fails safe). */
export function msSinceBoot(): number {
  try {
    return typeof performance === 'undefined' ? Infinity : performance.now()
  } catch {
    return Infinity
  }
}

export interface ProReloadInputs {
  /** adsOffDecision's answer. Only an explicit true can reload. */
  off: boolean | null
  /** ADS_KILL: the inline script already blocked everything. */
  kill: boolean
  /** adsBlockedAtBoot(): the page started blocked, so Journey never filled. */
  bootBlocked: boolean
  /** adsHaveLoaded(): there is something on the page to get rid of. */
  adsLoaded: boolean
  /** This tab already spent its reload for this user (ADS_OFF_RELOADED_KEY). */
  alreadyReloaded: boolean
  /** readAdsOffFlag() after the write: the reload will actually start blocked. */
  flagPersisted: boolean
  /** msSinceBoot() when the decision arrived. */
  elapsedMs: number
  /** userHasInteracted(): any trusted input on this page load so far. */
  interacted: boolean
  /**
   * handoffFieldsBusy(): a prerender handoff field (the deck box, the
   * commander search) holds text or focus, now or when it was handed over.
   * Belt and braces for the interaction guard.
   */
  formBusy: boolean
}

/**
 * The Pro first-load fix. Journey honours data-blocklist-all only when it is
 * on the settings div at first paint, and SPA navigation never reloads the
 * wrapper. A Pro reader whose browser had no cached flag (first visit on a
 * device, just signed in, Safari evicted storage) resolves as Pro AFTER the
 * ads filled, and would see them for the whole session. One reload, after
 * the flag is written, starts the page blocked.
 *
 * Reload ONLY when every one of these holds: the live answer is Pro, the
 * page did not already start blocked, ads are really on the page, the tab
 * has not reloaded for this user before, the flag stuck (with storage
 * failing, the reload would start unblocked again for nothing), the answer
 * arrived within PRO_RELOAD_DEADLINE_MS of navigation, the reader has not
 * touched the page yet (index.html's sentinel or this module's watch), and
 * no prerender handoff field holds text or focus. A reload must never eat a
 * half-typed or pasted decklist.
 * Never in the other direction: a Pro -> free change just shows ads on the
 * next load.
 */
export function shouldReloadForPro(i: ProReloadInputs): boolean {
  return i.off === true &&
    !i.kill &&
    !i.bootBlocked &&
    i.adsLoaded &&
    !i.alreadyReloaded &&
    i.flagPersisted &&
    i.elapsedMs <= PRO_RELOAD_DEADLINE_MS &&
    !i.interacted &&
    !i.formBusy
}

type SessionLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function defaultSession(): SessionLike | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}

/** True when this tab already reloaded for THIS user. */
export function readProReloadMarker(userId: string, storage: SessionLike | null = defaultSession()): boolean {
  try {
    return !!userId && storage?.getItem(ADS_OFF_RELOADED_KEY) === userId
  } catch {
    return false
  }
}

/**
 * Set the marker for this user and report whether it stuck. A false answer
 * means NO reload: without the marker there is no loop guard.
 */
export function writeProReloadMarker(userId: string, storage: SessionLike | null = defaultSession()): boolean {
  try {
    if (!storage || !userId) return false
    storage.setItem(ADS_OFF_RELOADED_KEY, userId)
    return storage.getItem(ADS_OFF_RELOADED_KEY) === userId
  } catch {
    return false
  }
}

/** Forget the marker (the answer is no longer Pro). */
export function clearProReloadMarker(storage: SessionLike | null = defaultSession()): void {
  try {
    storage?.removeItem(ADS_OFF_RELOADED_KEY)
  } catch {
    // Nothing to do; a stale marker only costs one skipped reload.
  }
}

/**
 * The Android app's one-time recovery reload (MediavineProSync). index.html
 * blocks all ads in-app, but its inline script runs after the async wrapper
 * tag, so a cached wrapper could in theory fill first. Seeing filled ads
 * in-app, the sync reloads ONCE so the next load starts blocked. Per tab, in
 * sessionStorage only: localStorage is shared with the reader's Chrome.
 */
export const ANDROID_ADS_RELOADED_KEY = 'cb.androidAdsReloaded'

/** True when this tab already did the Android recovery reload. */
export function readAndroidReloadMarker(storage: SessionLike | null = defaultSession()): boolean {
  try {
    return storage?.getItem(ANDROID_ADS_RELOADED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Set the Android marker and report whether it stuck. False means NO
 * reload: without the marker there is no loop guard.
 */
export function writeAndroidReloadMarker(storage: SessionLike | null = defaultSession()): boolean {
  try {
    if (!storage) return false
    storage.setItem(ANDROID_ADS_RELOADED_KEY, '1')
    return storage.getItem(ANDROID_ADS_RELOADED_KEY) === '1'
  } catch {
    return false
  }
}

/** The reload itself, behind a function so tests can stub it. */
export function reloadPage(win: Window = window): void {
  win.location.reload()
}

export interface SponsorVisibleInputs extends AdsOffInputs {
  /** ADS_KILL, passed in so the table is testable both ways. */
  kill: boolean
  /**
   * useSupabaseAuthFailed(): the initial session read REJECTED and no auth
   * event has answered since. useSupabaseToken reports that state as signed
   * out, which is the right call for its other callers but is not proof of
   * an anonymous visitor.
   */
  authFailed: boolean
  /** readAdsOffFlag(): the last resolved entitlement on this browser was Pro. */
  cachedOff: boolean
}

/**
 * Whether a direct-sold sponsor unit (src/components/partners/) may render.
 *
 * STRICTER THAN PROGRAMMATIC, on purpose, and deliberately NOT built on
 * `adsOffDecision` / `adsEnabled`. Those fail OPEN (unknown shows ads)
 * because Mediavine has a second line of defence: the inline script puts
 * data-blocklist-all on the settings div from the cached flag at first
 * paint, and the wrapper never fills for a Pro reader. A sponsor unit has no
 * such backstop. It is our own markup, it renders the instant React does,
 * and it bills its own impression, so "probably not Pro" puts a sponsor in
 * front of a paying customer and charges the sponsor for it. It shows ONLY
 * on positive evidence:
 *  - kill switch or session loading:                 hidden
 *  - signed in:   shown only on status 'success' AND is_pro === false
 *                 explicitly (pending, error, or a success with is_pro
 *                 missing are all hidden)
 *  - signed out:  shown only when the session read SUCCEEDED (or an auth
 *                 event answered), and the cached ads-off flag is clear.
 *                 A failed read looks signed out but proves nothing, and a
 *                 cached ex-Pro flag on a not-positively-resolved visitor
 *                 is belt and braces against exactly that case.
 * A signed-in visitor positively resolved as non-Pro ignores the cached flag:
 * that answer is newer, and MediavineProSync clears the flag from it.
 */
export function sponsorVisibleDecision(i: SponsorVisibleInputs): boolean {
  if (i.kill || i.loading) return false
  if (i.userId) return i.status === 'success' && i.isPro === false
  return !i.authFailed && !i.cachedOff
}

type CmpApi = (command: string) => void

interface CmpWindow {
  __cmp?: CmpApi
}

/**
 * Reopen Mediavine's consent dialog so an EU/UK visitor can change their
 * choice, through consentmanager's own `__cmp('showScreen')` - the vendor
 * Mediavine's CMP runs on. Deliberately NOT `__tcfapi('displayConsentUi')`:
 * that is Google Funding Choices' command, not part of IAB TCF v2, so a TCF
 * CMP can accept the call and silently ignore it, and we would have resolved
 * true and never fallen back. Resolves false when `__cmp` is not on the
 * window (wrapper blocked, not yet loaded, or a visitor outside the consent
 * regions where the CMP never installs), so the footer can fall through to
 * /privacy instead of clicking into silence.
 */
export function openPrivacyChoices(win: Window = window): Promise<boolean> {
  const w = win as unknown as CmpWindow
  if (typeof w.__cmp === 'function') {
    try {
      // eslint-disable-next-line i18next/no-literal-string -- CMP command name
      w.__cmp('showScreen')
      return Promise.resolve(true)
    } catch {
      return Promise.resolve(false)
    }
  }
  return Promise.resolve(false)
}
